'use client';

import React, { useCallback } from 'react';
import { AppImage, DotsIndicator, Icon, Typography } from '@workspace/ui/common';
import { sanityImageSrc } from '@/sanity/lib/image';
import { Gallery } from '@workspace/blocks/Gallery';
import { cn } from '@workspace/lib';
import { useProductDetailCarousel } from './useProductDetailCarousel';

interface ProductImageCarouselProps {
  carouselImages: any[];
  galleryImages: any[];
}

export const ProductImageCarousel = ({ carouselImages, galleryImages }: ProductImageCarouselProps) => {
  const {
    emblaRef,
    validImages,
    currentIndex,
    isFullscreen,
    setIsFullscreen,
    handlePrevious,
    handleNext,
    handleThumbnailClick,
    isImageLoaded,
    suppressTransition,
  } = useProductDetailCarousel(carouselImages);

  const handleImageClick = useCallback(() => setIsFullscreen(true), [setIsFullscreen]);
  const handleCloseFullscreen = useCallback(() => setIsFullscreen(false), [setIsFullscreen]);

  if (!carouselImages || carouselImages.length === 0) {
    return (
      <div className="w-full h-96 bg-secondary-200 rounded-lg flex items-center justify-center">
        <span className="text-secondary-50">No images available</span>
      </div>
    );
  }

  if (validImages.length === 0) {
    return (
      <div className="w-full h-96 bg-secondary-200 rounded-lg flex items-center justify-center">
        <span className="text-secondary-50">No valid images available</span>
      </div>
    );
  }

  return (
    <>
      {/* Main Carousel */}
      <div className="space-y-4">
        {/* Main Image as Embla */}
        <div className="relative w-full !h-82 md:!h-122 overflow-hidden">
          <div className="embla h-full overflow-hidden" ref={emblaRef}>
            <div className="embla__container flex h-full will-change-transform transform-gpu touch-pan-y select-none">
              {validImages.map((img, index) => {
                return (
                  <div key={img._key} className="embla__slide flex-[0_0_100%] min-w-0 h-full relative">
                    {img && (
                      <div className="image-primary">
                        <AppImage
                          src={sanityImageSrc(img, { width: 489, height: 489 })}
                          alt={img?.alt || `Product image ${index + 1}`}
                          width={489}
                          height={489}
                          className={cn(
                            'w-full !h-82 md:!h-122 object-cover cursor-pointer',
                            suppressTransition ? 'opacity-100' : 'transition-opacity duration-300',
                            isImageLoaded(index) ? 'opacity-100' : 'opacity-0',
                          )}
                          onClick={handleImageClick}
                          priority={index === currentIndex}
                          loading={index === currentIndex ? 'eager' : 'lazy'}
                          draggable={false}
                        />
                        {!suppressTransition && !isImageLoaded(index) && (
                          <div className="absolute inset-0 flex items-center justify-center bg-secondary-200">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Navigation Arrows */}
          {validImages.length > 1 && (
            <>
              <div
                className="icon-container hidden md:block p-3 absolute left-4 top-1/2 cursor-pointer -translate-y-1/2 select-none"
                onMouseDown={(e) => e.preventDefault()}
              >
                <Icon name="ChevronLeft" onClick={handlePrevious} size={24} className="text-primary" />
              </div>

              <div
                className="icon-container hidden md:block p-3 absolute right-4 top-1/2 cursor-pointer -translate-y-1/2 select-none"
                onMouseDown={(e) => e.preventDefault()}
              >
                <Icon name="ChevronRight" onClick={handleNext} size={24} className="text-primary" />
              </div>
            </>
          )}
        </div>

        {/* Dot Navigation - Mobile Only */}
        <DotsIndicator
          className="justify-center md:hidden"
          count={validImages.length}
          currentIndex={currentIndex}
          onDotClick={handleThumbnailClick}
        />

        {/* Thumbnail Navigation - Desktop Only */}
        {validImages.length > 1 && (
          <div className="hidden md:flex gap-3 justify-center">
            {(validImages.length <= 6 ? validImages : validImages.slice(0, 5)).map((image, index) => {
              return (
                <div
                  key={image._key}
                  onClick={() => handleThumbnailClick(index)}
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  className={cn(
                    'flex-shrink-0 size-18 overflow-hidden cursor-pointer',
                    index === currentIndex && currentIndex < validImages.length ? 'border border-primary' : '',
                  )}
                >
                  {image ? (
                    <AppImage
                      src={sanityImageSrc(image, { width: 80, height: 80 })}
                      alt={image.alt || `Thumbnail ${index + 1}`}
                      width={80}
                      height={80}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-secondary-200 flex items-center justify-center text-secondary-50 text-xs">
                      No Image
                    </div>
                  )}
                </div>
              );
            })}
            {validImages.length > 6 &&
              (() => {
                const sixth = validImages[5];
                return (
                  <div
                    onClick={() => setIsFullscreen(true)}
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    className="relative flex-shrink-0 size-18 overflow-hidden cursor-pointer select-none"
                    aria-label={`Open gallery with ${validImages.length} images`}
                  >
                    {sixth ? (
                      <AppImage
                        src={sanityImageSrc(sixth, { width: 80, height: 80 })}
                        alt="More images"
                        width={80}
                        height={80}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-secondary-200" />
                    )}
                    <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
                      <Typography variant="h6" className="text-white font-bold text-sm">
                        +{validImages.length - 5}
                      </Typography>
                    </div>
                  </div>
                );
              })()}
          </div>
        )}
      </div>

      {/* Fullscreen: use shared Gallery component */}
      {isFullscreen && (
        <Gallery
          block={{
            _key: 'product-gallery-fullscreen',
            _type: 'gallery',
            images: galleryImages,
            showNavigationArrows: true,
            navIconSize: 24,
          }}
          index={0}
          isOpen={true}
          startIndex={galleryImages.findIndex((img: any) => img._key === validImages[currentIndex]?._key)}
          onRequestClose={handleCloseFullscreen}
        />
      )}
    </>
  );
};
